import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import AppButton from 'components/AppButton/AppButton';
import { TargetGroupCsvDataRow } from 'typings/user';
import { CSVLink } from 'react-csv';
import { DownloadIcon } from 'evergreen-ui';
import {
    useLazyGetParticipantsActivityQuery,
    useLazyGetParticipantsQuery,
} from '../../../store/api/participants';
import { ParticipantWithActivity } from '../../../typings/participant_activity';
import { formatScores, formatSession } from './utils';
import LinearProgress from '@material-ui/core/LinearProgress';

export interface TargetGroupScoreDownloadProps {
    companyId: string;
    groupId: string;
    assessmentName: string;
    targetGroupName: string;
    participantsCount: number;
}

const TargetGroupScoreDownload: FunctionComponent<
    TargetGroupScoreDownloadProps
> = (props) => {
    const {
        companyId,
        groupId,
        assessmentName,
        targetGroupName,
        participantsCount,
    } = props;

    const csvLink = useRef<
        CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
    >(null);
    const [csvData, setCsvData] = useState<TargetGroupCsvDataRow[]>([]);

    const [getParticipants] = useLazyGetParticipantsQuery();
    const [getParticipantsActivity] = useLazyGetParticipantsActivityQuery();

    const [isFetching, setIsFetching] = useState(false);
    const [isDone, setIsDone] = useState(false);
    const [processedCount, setProcessedCount] = useState<number>(0);

    function wait(ms: number) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    const downloadScores = async () => {
        const limit = 80;

        const finalData: Record<string, ParticipantWithActivity> = {};

        const getData = async (nextToken: string) => {
            try {
                const { participants, nextToken: nextNextToken } =
                    await getParticipants({
                        companyIdentifier: companyId,
                        groupId,
                        limit,
                        includeResults: false,
                        includeTotal: false,
                        nextToken,
                    }).unwrap();

                const participantsIds = participants.map((p) => ({
                    userId: p.user_id,
                    invitationId: p.invitation_id,
                }));

                const participantsActivity = await getParticipantsActivity({
                    companyIdentifier: companyId,
                    groupId,
                    userIds: participantsIds.map((p) => p.userId),
                }).unwrap();

                for (const participantIds of participantsIds) {
                    const userId = participantIds.userId;

                    const participant = participants.find(
                        (p) => p.user_id === participantIds.userId,
                    );

                    const participantActivity = participantsActivity.find(
                        (p) => p.invitation.id === participantIds.invitationId,
                    );

                    if (participant && participantActivity) {
                        finalData[userId] = {
                            ...participant,
                            ...participantActivity,
                        };
                    }
                }

                setProcessedCount((prev) => prev + participants.length);

                if (nextNextToken) {
                    await wait(1000);
                    await getData(nextNextToken);
                }
            } catch (e) {
                console.log('Error getting data', e); // todo
            }
        };

        setIsFetching(true);
        setProcessedCount(0);
        await getData('');

        if (Object.keys(finalData).length) {
            const scoresArray: TargetGroupCsvDataRow[] = [];

            for (const userId in finalData) {
                const { scores, sessions } = finalData[userId];
                if (scores || sessions) {
                    const scoreDataCurrentParticipant: TargetGroupCsvDataRow = {
                        Scan: assessmentName,
                        'Target Group': targetGroupName,
                        user_id: finalData[userId].user_id,
                        Email: finalData[userId].email,
                        'Candidate name':
                            finalData[userId].first_name +
                            ' ' +
                            finalData[userId].last_name,
                    };

                    if (scores) {
                        formatScores(scores, scoreDataCurrentParticipant);
                    }

                    if (sessions) {
                        formatSession(sessions, scoreDataCurrentParticipant);
                    }

                    scoresArray.push(scoreDataCurrentParticipant);
                }
            }

            const filteredCsvData = scoresArray.filter(
                (scoreDataOfCurrentParticipant) => {
                    return (
                        !!scoreDataOfCurrentParticipant &&
                        (!!scoreDataOfCurrentParticipant['Date completed'] ||
                            !!scoreDataOfCurrentParticipant.Engagement ||
                            !!scoreDataOfCurrentParticipant['Culture Match'] ||
                            !!scoreDataOfCurrentParticipant.Adhocracy ||
                            !!scoreDataOfCurrentParticipant.Hierarchy ||
                            !!scoreDataOfCurrentParticipant.Market ||
                            !!scoreDataOfCurrentParticipant.Family ||
                            !!scoreDataOfCurrentParticipant.Confidence ||
                            !!scoreDataOfCurrentParticipant.Motivation ||
                            !!scoreDataOfCurrentParticipant[
                                'Stress resilience'
                            ] ||
                            !!scoreDataOfCurrentParticipant[
                                'Ease under pressure'
                            ] ||
                            !!scoreDataOfCurrentParticipant[
                                'General knowledge'
                            ] ||
                            !!scoreDataOfCurrentParticipant[
                                'Verbal reasoning'
                            ] ||
                            !!scoreDataOfCurrentParticipant[
                                'Numerical reasoning'
                            ] ||
                            !!scoreDataOfCurrentParticipant[
                                'Logical reasoning'
                            ])
                    );
                },
            );

            setCsvData(filteredCsvData);
            setIsDone(true);
        }
    };

    useEffect(() => {
        if (csvData.length && isFetching) {
            csvLink?.current?.link.click();
            setIsFetching(false);
            setProcessedCount(0);
        }
    }, [csvData, isFetching]);

    const progress = participantsCount
        ? Math.min((processedCount / participantsCount) * 100, 100)
        : 0;

    return (
        <div aria-label="download score button">
            {!isDone && isFetching && (
                <div>
                    <LinearProgress variant="determinate" value={progress} />
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                        Downloading scores…
                    </div>
                </div>
            )}
            {(isDone || !isFetching) && (
                <AppButton
                    justifyContent={'flex-start'}
                    width={'auto'}
                    icon={DownloadIcon}
                    isDisabled={isFetching}
                    onClick={downloadScores}>
                    Download all scores
                </AppButton>
            )}
            <CSVLink
                data={csvData}
                filename={targetGroupName + '.csv'}
                className="hidden"
                ref={csvLink}
                target={'_blank'}
            />
        </div>
    );
};

export default TargetGroupScoreDownload;
